/**
 * Pure permission-string helpers shared by web and (eventually) mobile.
 *
 * These are NOT React hooks — they are plain functions usable anywhere
 * (components, hooks, services). They centralize the raw
 * `permissions.includes('system:admin')` checks that were previously
 * copy-pasted across ~20 components.
 *
 * Semantics cheat-sheet (pick the one that matches the call site — they are
 * deliberately NOT interchangeable):
 *  - hasPermission:        key OR system:admin (the dominant UI gating pattern;
 *                          system admins implicitly pass).
 *  - hasExactPermission:   key only — NO system:admin auto-grant. Use when the
 *                          backend gate is strict or the site deliberately
 *                          excludes system admins.
 *  - hasAnyPermission:     any of the keys OR system:admin.
 *  - isSystemAdmin:        bare system:admin flag.
 *  - isSystemOrAccountAdmin: system:admin OR account:admin.
 *  - isSystemAdminOrUnscoped: DEPRECATED — system:admin OR an EMPTY permission
 *                          set. An empty set can be a real answer (a location
 *                          where someone holds nothing), so gates use
 *                          isSystemAdmin / hasPermission instead.
 */

export const SYSTEM_ADMIN_PERMISSION = 'system:admin';
export const ACCOUNT_ADMIN_PERMISSION = 'account:admin';

type Permissions = readonly string[] | null | undefined;

/**
 * Plain membership check — NO system:admin auto-grant.
 * `null`/`undefined` permission lists are treated as empty (false).
 */
export function hasExactPermission(permissions: Permissions, key: string): boolean {
  return (permissions || []).includes(key);
}

/**
 * True when the permission set contains `key` OR `system:admin`.
 * This mirrors the most common UI gating pattern:
 *   perms.includes(key) || perms.includes('system:admin')
 */
export function hasPermission(permissions: Permissions, key: string): boolean {
  const perms = permissions || [];
  return perms.includes(key) || perms.includes(SYSTEM_ADMIN_PERMISSION);
}

/**
 * True when the permission set contains ANY of `keys` OR `system:admin`.
 */
export function hasAnyPermission(permissions: Permissions, keys: readonly string[]): boolean {
  const perms = permissions || [];
  return perms.includes(SYSTEM_ADMIN_PERMISSION) || keys.some((key) => perms.includes(key));
}

/**
 * Bare `system:admin` flag. `null`/`undefined` permission lists → false.
 */
export function isSystemAdmin(permissions: Permissions): boolean {
  return (permissions || []).includes(SYSTEM_ADMIN_PERMISSION);
}

/**
 * `system:admin` OR `account:admin`. Common "is some kind of admin" gate
 * on account/location edit affordances.
 */
export function isSystemOrAccountAdmin(permissions: Permissions): boolean {
  const perms = permissions || [];
  return perms.includes(SYSTEM_ADMIN_PERMISSION) || perms.includes(ACCOUNT_ADMIN_PERMISSION);
}

/**
 * @deprecated system:admin OR an EMPTY permission set (`null`/`undefined`
 * count as empty). An empty list is a real answer (no grants), not a trusted
 * caller, so treating it as system admin unlocked gates for anyone whose
 * permission set came back empty. No gate uses it any more; use
 * isSystemAdmin / hasPermission / hasExactPermission.
 */
export function isSystemAdminOrUnscoped(permissions: Permissions): boolean {
  const perms = permissions || [];
  return perms.length === 0 || perms.includes(SYSTEM_ADMIN_PERMISSION);
}

/**
 * May resend an invoice (single, per delivery, or bulk). Matches the API:
 * POST /invoice-batches/:id/resend, /invoice-deliveries/:id/resend and
 * /invoice-batches/bulk-resend require invoices:send or account:admin
 * (system admins pass everywhere).
 */
export function canResendInvoices(permissions: Permissions): boolean {
  return (
    isSystemAdmin(permissions) ||
    hasExactPermission(permissions, 'invoices:send') ||
    hasExactPermission(permissions, ACCOUNT_ADMIN_PERMISSION)
  );
}

/**
 * May open an invoice. Matches the API (invoice_view_access.go): View invoices
 * for all locations or assigned locations (invoices:view:account|location),
 * sending invoices, editing the invoice rules, accounting, or account admin. The server
 * also checks the invoice's location; this is the session-wide answer, so a
 * location-level holder may still be refused another store's invoice. Others
 * see the invoice number only.
 */
export function canOpenInvoices(permissions: Permissions): boolean {
  return (
    canResendInvoices(permissions) ||
    ['invoices:view:account', 'invoices:view:location', 'invoice:rules:edit', 'accounting:view', 'accounting:configure'].some((p) =>
      hasExactPermission(permissions, p)
    )
  );
}
